/**
 * The table you play on: the canvas, and laid over it a button for every
 * thing you can act on right now, a quiet zone for everything else you can
 * point at, the tooltip, and the prompt bar.
 *
 * The buttons are real buttons, placed from the same rectangles the canvas
 * draws with, so the mouse, the keyboard and a screen reader reach exactly the
 * same moves — the ones `offer` found in `legalCommands`, and no others.
 */
import type { Command, GameEvent, PlayerId, View } from '@trajan/engine';
import {
  useEffect,
  useMemo,
  useRef,
  useState,
  type KeyboardEvent,
  type PointerEvent,
  type ReactNode,
} from 'react';
import { matShow } from '../mat/matShow';
import { argOf, kindOf, type TargetId } from '../table/ids';
import { MAT, PROMPT, TABLE_H, TABLE_W, percentOf, tableLayout, type Rect } from '../table/layout';
import type { SceneInput, TableScene } from '../table/scene';
import { TableCanvas } from '../table/TableCanvas';
import { NONE, markerColour, nudgeMarker, swapIn, type Holding } from './holding';
import { inspect } from './inspect';
import { matFor } from './matFor';
import { offer, type Act, type Offer, type Target } from './offer';

/** Things that belong to the tableau on show, not the board: not live while peeking at another seat. */
const TABLEAU_KINDS = new Set([
  'tray',
  'slot',
  'marker',
  'plus2',
  'vp',
  'supply',
  'camp',
  'held',
  'card',
  'display',
]);

/** Far enough from where the pointer went down to mean a drag, not a tap. */
const DRAG_THRESHOLD = 5;

export interface TableProps {
  view: View;
  /** The seat this screen plays for, or null for someone only watching. */
  actor: PlayerId | null;
  /** Whose tableau is on show. */
  shown: PlayerId;
  nameOf: (seat: PlayerId) => string;
  onSend: (command: Command) => void;
  /** What the prompt bar says when the game is waiting for someone else. */
  waiting: string;
  /** More for the prompt bar to say — the quarter-end demand standing. */
  extra?: ReactNode;
  /** Laid over the table: the scoring table at the end. */
  overlay?: ReactNode;
  /** For previews and screenshots: something already in hand, a target already pointed at. */
  initial?: { holding?: Holding | undefined; hover?: TargetId | undefined } | undefined;
  /** The game log, so what happens next can be played out on the table. */
  log?: readonly GameEvent[] | undefined;
}

export function Table({
  view,
  actor,
  shown,
  nameOf,
  onSend,
  waiting,
  extra,
  overlay,
  initial,
  log,
}: TableProps) {
  const [raw, setRaw] = useState<Holding>(initial?.holding ?? NONE);
  const [seq, setSeq] = useState(view.seq);
  // Whatever was held belongs to the view it was picked up in.
  if (seq !== view.seq) {
    setSeq(view.seq);
    setRaw(NONE);
  }
  const [hover, setHover] = useState<TargetId | null>(initial?.hover ?? null);
  const [drag, setDrag] = useState<{ index: number; x: number; y: number } | null>(null);
  const frame = useRef<HTMLDivElement>(null);
  const scene = useRef<TableScene | null>(null);
  const focusNext = useRef<TargetId | null>(null);
  /** Whether the keyboard focus is somewhere in the table — kept when its button is removed. */
  const focusInside = useRef(false);
  const down = useRef<{ index: number; x: number; y: number; dragging: boolean } | null>(null);
  const suppressClick = useRef(false);

  const o = useMemo(() => offer(view, actor, raw), [view, actor, raw]);
  // The log's new entries since the table last saw it: played once, then old news.
  const seen = useRef(log?.length ?? 0);
  const events = useMemo(() => {
    const from = seen.current;
    const to = log?.length ?? 0;
    return log && to > from ? { from, list: log.slice(from, to) } : undefined;
  }, [log]);
  useEffect(() => {
    seen.current = log?.length ?? 0;
  }, [log]);
  const mine = actor !== null && shown === actor;
  const player = view.players[shown];
  const hoverTray = hover && kindOf(hover) === 'tray' ? Number(argOf(hover)) : null;

  const mat = useMemo(() => {
    if (!player) return null;
    return matShow(
      player,
      mine
        ? matFor(view, o.holding, { hover: hoverTray, dragging: drag?.index ?? null })
        : { hover: hoverTray },
    );
  }, [player, mine, view, o.holding, hoverTray, drag?.index]);

  const rects = useMemo(() => tableLayout(view, shown, mat?.markers ?? []), [view, shown, mat]);
  const targets = useMemo(
    () => (mine ? o.targets : o.targets.filter((t) => !TABLEAU_KINDS.has(kindOf(t.id)))),
    [o.targets, mine],
  );
  const live = useMemo(() => new Map(targets.map((t) => [t.id, t])), [targets]);

  const input: SceneInput | null = useMemo(
    () =>
      mat
        ? {
            view,
            seat: shown,
            mat,
            live: new Map(targets.map((t) => [t.id, t.tone])),
            rects,
            raised: mine ? o.raised : new Set<string>(),
            owner: shown === actor ? 'your tableau' : `${nameOf(shown)}’s tableau`,
            ships: o.ships,
            stacks: stackMarks(o.holding),
            events,
          }
        : null,
    [view, shown, mat, targets, rects, mine, o.raised, o.ships, actor, nameOf, o.holding, events],
  );

  useEffect(() => {
    scene.current?.hover(hover);
  }, [hover, input]);

  useEffect(() => {
    const color = drag ? markerColour(o.holding, drag.index) : null;
    scene.current?.drag(drag && color ? { color, x: drag.x, y: drag.y } : null);
  }, [drag, o.holding]);

  // After the arrow keys move a marker, the focus follows it to its new button.
  // And when a move takes away the button the focus was on — a tray picked up,
  // a tile taken — the focus goes to the first thing that can be done next,
  // rather than falling out of the table to the top of the page.
  useEffect(() => {
    const id = focusNext.current;
    if (id) {
      focusNext.current = null;
      frame.current?.querySelector<HTMLElement>(`[data-target="${id}"]`)?.focus();
      return;
    }
    if (!focusInside.current || document.activeElement !== document.body) return;
    frame.current?.querySelector<HTMLElement>('button.hit, .prompt button:not(:disabled)')?.focus();
  });

  const act = (a: Act) => {
    if ('send' in a) {
      setRaw(NONE);
      onSend(a.send);
    } else {
      setRaw(a.hold);
    }
  };

  /** Pointer position in table units. */
  const toTable = (e: PointerEvent): { x: number; y: number } => {
    const r = frame.current?.getBoundingClientRect();
    if (!r || r.width === 0) return { x: 0, y: 0 };
    return {
      x: ((e.clientX - r.left) / r.width) * TABLE_W,
      y: ((e.clientY - r.top) / r.height) * TABLE_H,
    };
  };

  const nearestMarker = (at: { x: number; y: number }, not: number): number | null => {
    let best: number | null = null;
    let bestDist = 26;
    for (const t of targets) {
      if (kindOf(t.id) !== 'marker') continue;
      const index = Number(argOf(t.id));
      const r = rects.get(t.id);
      if (!r || index === not) continue;
      const d = Math.hypot(r.x + r.w / 2 - at.x, r.y + r.h / 2 - at.y);
      if (d < bestDist) {
        best = index;
        bestDist = d;
      }
    }
    return best;
  };

  const markerHandlers = (t: Target) => {
    const index = Number(argOf(t.id));
    return {
      onPointerDown: (e: PointerEvent<HTMLButtonElement>) => {
        if (e.button !== 0) return;
        e.currentTarget.setPointerCapture(e.pointerId);
        const at = toTable(e);
        down.current = { index, x: at.x, y: at.y, dragging: false };
      },
      onPointerMove: (e: PointerEvent<HTMLButtonElement>) => {
        const d = down.current;
        if (!d) return;
        const at = toTable(e);
        if (!d.dragging && Math.hypot(at.x - d.x, at.y - d.y) < DRAG_THRESHOLD) return;
        d.dragging = true;
        setDrag({ index: d.index, x: at.x, y: at.y });
      },
      onPointerUp: (e: PointerEvent<HTMLButtonElement>) => {
        const d = down.current;
        down.current = null;
        if (!d?.dragging) return;
        suppressClick.current = true;
        const onto = nearestMarker(toTable(e), d.index);
        setDrag(null);
        if (onto !== null) setRaw(swapIn(o.holding, d.index, onto));
      },
      onPointerCancel: () => {
        down.current = null;
        setDrag(null);
      },
      onKeyDown: (e: KeyboardEvent<HTMLButtonElement>) => {
        const step = e.key === 'ArrowRight' ? 1 : e.key === 'ArrowLeft' ? -1 : 0;
        if (step === 0 || o.holding.kind !== 'tray') return;
        e.preventDefault();
        const moved = nudgeMarker(o.holding, index, step);
        focusNext.current = `marker:${moved.index}`;
        setRaw(moved.holding);
      },
    };
  };

  const onKeyDown = (e: KeyboardEvent<HTMLDivElement>) => {
    if (e.key !== 'Escape') return;
    const k = o.holding.kind;
    if (k === 'tray' || k === 'cards' || k === 'discard' || k === 'plus2' || k === 'wild') {
      e.preventDefault();
      setRaw(NONE);
    }
  };

  const hovered = hover ? { id: hover, rect: rects.get(hover) } : null;

  return (
    <div
      className="table-frame"
      ref={frame}
      onKeyDown={onKeyDown}
      onFocus={() => {
        focusInside.current = true;
      }}
      onBlur={(e) => {
        // Cleared only when the focus really leaves the table — tabbed or clicked
        // out of it — and not when its button was simply taken away by a move.
        const left = e.target;
        const box = e.currentTarget;
        queueMicrotask(() => {
          if (left.isConnected && !box.contains(document.activeElement)) {
            focusInside.current = false;
          }
        });
      }}
    >
      <TableCanvas input={input} sceneRef={scene} />
      <div className="hit-layer" role="group" aria-label="The table">
        {[...rects].map(([id, r]) => {
          const t = live.get(id);
          if (t) return null;
          return (
            <div
              key={id}
              className="zone"
              style={percentOf(r)}
              aria-hidden="true"
              onPointerEnter={(e) => e.pointerType === 'mouse' && setHover(id)}
              onPointerLeave={() => setHover((h) => (h === id ? null : h))}
            />
          );
        })}
        {targets.map((t) => {
          const r = rects.get(t.id);
          if (!r) return null;
          const marker = kindOf(t.id) === 'marker';
          return (
            <button
              key={t.id}
              type="button"
              data-target={t.id}
              className={`hit hit-${t.tone}${marker ? ' hit-marker' : ''}`}
              style={percentOf(r)}
              aria-label={spoken(t)}
              aria-pressed={t.tone === 'picked' ? true : undefined}
              onPointerEnter={(e) => e.pointerType === 'mouse' && setHover(t.id)}
              onPointerLeave={() => setHover((h) => (h === t.id ? null : h))}
              onFocus={() => setHover(t.id)}
              onBlur={() => setHover((h) => (h === t.id ? null : h))}
              onClick={() => {
                if (suppressClick.current) {
                  suppressClick.current = false;
                  return;
                }
                act(t.act);
              }}
              {...(marker ? markerHandlers(t) : {})}
            />
          );
        })}
      </div>

      {hovered?.rect && !drag && (
        <Tip
          rect={hovered.rect}
          target={live.get(hovered.id) ?? null}
          note={inspect(view, shown, hovered.id, nameOf)}
        />
      )}

      {o.chooser && rects.get(o.chooser.anchor) && (
        <div
          className="chooser"
          style={chooserAt(rects.get(o.chooser.anchor)!)}
          role="menu"
          aria-label={o.chooser.title}
        >
          <p>{o.chooser.title}</p>
          <div className="chooser-options">
            {o.chooser.options.map((c) => (
              <button key={c.key} type="button" role="menuitem" onClick={() => act(c.act)}>
                {c.label}
              </button>
            ))}
          </div>
          <button
            type="button"
            className="chooser-cancel"
            onClick={() =>
              act({
                hold: o.holding.kind === 'cards' ? { ...o.holding, wildFor: null } : o.holding,
              })
            }
          >
            Cancel
          </button>
        </div>
      )}

      <PromptBar
        offer={o}
        waiting={waiting}
        extra={extra}
        onAct={act}
        watching={actor === null}
        over={view.phase.kind === 'gameOver'}
      />
      {overlay}
    </div>
  );
}

/** At setup, which stacks the tiles in hand or on the mat came from. */
function stackMarks(holding: Holding): ReadonlyMap<number, 'held' | number> | undefined {
  if (holding.kind !== 'tiles') return undefined;
  const marks = new Map<number, 'held' | number>();
  for (const [slot, stack] of Object.entries(holding.plan.placed)) marks.set(stack, Number(slot));
  if (holding.plan.holding !== null) marks.set(holding.plan.holding, 'held');
  return marks;
}

/** A live target's whole sentence, for a screen reader: what it does, and what it is worth. */
function spoken(t: Target): string {
  return [t.title, t.detail, t.vp !== undefined ? `${t.vp} VP` : undefined]
    .filter(Boolean)
    .join('. ');
}

function chooserAt(r: Rect) {
  const p = percentOf({ x: r.x + r.w / 2, y: r.y, w: 0, h: 0 });
  return { left: p.left, top: p.top };
}

function Tip({
  rect,
  target,
  note,
}: {
  rect: Rect;
  target: Target | null;
  note: ReturnType<typeof inspect>;
}) {
  if (!target && !note) return null;
  const onMat = rect.x >= MAT.x && rect.x + rect.w <= MAT.x + MAT.w && rect.y >= MAT.y;
  const below = rect.y < 190;
  const anchor = onMat
    ? percentOf({
        x: MAT.x + MAT.w + 10,
        y: Math.min(rect.y + rect.h / 2, MAT.y + MAT.h - 70),
        w: 0,
        h: 0,
      })
    : percentOf({ x: rect.x + rect.w / 2, y: below ? rect.y + rect.h : rect.y, w: 0, h: 0 });
  const edge =
    rect.x + rect.w / 2 < 160 ? 'tip-left' : rect.x + rect.w / 2 > TABLE_W - 160 ? 'tip-right' : '';
  const said = target?.detail?.toLowerCase();
  const lines = (note?.lines ?? []).filter((l) => l.toLowerCase().replace(/\.$/, '') !== said);
  return (
    <div
      className={`tip ${onMat ? 'tip-side' : `${below ? 'tip-below' : 'tip-above'} ${edge}`}`}
      style={{ left: anchor.left, top: anchor.top }}
      role="tooltip"
    >
      {target && (
        <p className={`tip-act tip-act-${target.tone}`}>
          <span>{target.title}</span>
          {target.vp !== undefined && <b className="tip-vp">{target.vp} VP</b>}
        </p>
      )}
      {target?.detail && <p className="tip-detail">{target.detail}</p>}
      {note && (
        <div className="tip-note">
          <p className="tip-title">{note.title}</p>
          {lines.map((l, i) => (
            <p key={i}>{l}</p>
          ))}
        </div>
      )}
    </div>
  );
}

function PromptBar({
  offer: o,
  waiting,
  extra,
  onAct,
  watching,
  over,
}: {
  offer: Offer;
  waiting: string;
  extra: ReactNode;
  onAct: (a: Act) => void;
  watching: boolean;
  over: boolean;
}) {
  const title = o.yours || over ? o.title : watching ? 'Watching' : 'Waiting';
  const hint = o.yours || over ? o.hint : waiting;
  return (
    <div
      className={`prompt${o.yours ? ' prompt-yours' : ''}`}
      style={percentOf(PROMPT)}
      role="status"
      aria-live="polite"
    >
      <div className="prompt-words">
        <span className="prompt-title">{title}</span>
        <span className="prompt-hint">{hint}</span>
        {extra}
      </div>
      {o.buttons.length > 0 && (
        <div className="prompt-buttons">
          {o.buttons.map((b) => (
            <button
              key={b.key}
              type="button"
              className={b.primary ? 'prompt-go' : 'prompt-quiet'}
              disabled={b.act === null}
              title={b.detail ?? ''}
              onClick={() => b.act && onAct(b.act)}
            >
              {b.label}
              {b.detail && b.primary ? <small>{b.detail}</small> : null}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
